/**
 * Help & support — the pure rules (contract `dealhunt/docs/sg-owner-support-contract.md` §5):
 * addresses, the status words, the form's limits, the page the reader came from, and the list
 * order. No React, no fetch. Tests: `tests/support-model.spec.ts`.
 *
 * Limits are DealHunt's (§1: subject ≤ 120 on one line, body ≤ 5 000, ≤ 4 screenshots a message,
 * ≤ 4 MB each, jpeg/png/webp); the minimums (subject 3, a new ticket's message 10) are SoundGuys'
 * form floor so a stray keypress is not a ticket — a reply on an existing thread needs 1 character
 * (DealHunt's own floor; "Thanks!" is a reply). Lengths are UTF-16 units (never more than DealHunt's code
 * points).
 */
import type { TicketCard, TicketMessage, TicketStatus } from './support-types'

export const SUPPORT_PATH = '/profile/support'

/** The only origin a captured `page_url` may name (§1: https on www.soundguys.com only). */
const SITE_ORIGIN = 'https://www.soundguys.com'

export const SUPPORT_LIMITS = {
  subjectMin: 3,
  subjectMax: 120,
  bodyMin: 10,
  replyBodyMin: 1,
  bodyMax: 5000,
  images: 4,
  imageBytes: 4 * 1024 * 1024,
  pageUrl: 500,
} as const

export const SUPPORT_IMAGE_TYPES: readonly string[] = ['image/jpeg', 'image/png', 'image/webp']

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/

function withSlash(path: string, trailingSlash: boolean): string {
  return trailingSlash ? `${path}/` : path
}

/** `/profile/support`, optionally with `?from=<path>` — the page the reader opened it from. */
export function supportHref(trailingSlash: boolean, from?: string | null): string {
  const base = withSlash(SUPPORT_PATH, trailingSlash)
  const path = fromPath(from)
  return path ? `${base}?from=${encodeURIComponent(path)}` : base
}

/** One ticket's page. */
export function ticketHref(id: string, trailingSlash: boolean): string {
  return withSlash(`${SUPPORT_PATH}/${encodeURIComponent(id)}`, trailingSlash)
}

/** A ticket id from the URL: a UUID, lowercased, or null (→ 404 before any fetch). */
export function ticketIdParam(raw: unknown): string | null {
  const id = typeof raw === 'string' ? raw.toLowerCase() : ''
  return UUID_RE.test(id) ? id : null
}

export function ticketStatusLabel(status: TicketStatus): string {
  switch (status) {
    case 'answered':
      return 'Answered'
    case 'closed':
      return 'Closed'
    default:
      return 'Open'
  }
}

/** What the reader is told on the form and on an open ticket (contract §0). */
export function replyWindowLine(): string {
  return 'We usually reply within 1–2 days.'
}

/** The thread's status line. */
export function ticketStatusLine(status: TicketStatus): string {
  switch (status) {
    case 'answered':
      return 'Support replied'
    case 'closed':
      return 'Closed — reply to reopen'
    default:
      return replyWindowLine()
  }
}

/** "1 message" / "3 messages". */
export function messageCountLabel(count: number): string {
  const n = Number.isFinite(count) && count > 0 ? Math.floor(count) : 0
  return `${n} ${n === 1 ? 'message' : 'messages'}`
}

/**
 * The bubble's name: "You" on the reader's own, always "SoundGuys support" on staff's. DealHunt's
 * `staff_name` is the admin's email local-part (admin users have no display name) — it is never
 * shown to a reader (decided 2026-10-04); the field stays in the types only.
 */
export function messageAuthorName(message: Pick<TicketMessage, 'author' | 'staff_name'>): string {
  return message.author === 'reader' ? 'You' : 'SoundGuys support'
}

/** A subject is one line (§1): line breaks become spaces, runs collapse, ends trimmed. */
export function cleanSubject(subject: string): string {
  return subject.replace(/\s+/g, ' ').trim()
}

/** A screenshot tile's state as the form needs it for `canSubmit`. */
export interface SupportImageState {
  status: 'uploading' | 'done' | 'error'
}

export interface SupportDraft {
  /** null on a reply (no subject). */
  subject: string | null
  body: string
  images: readonly SupportImageState[]
}

/**
 * Why the form can't be sent yet, or null. Subject 3–120 (a new ticket only), message 10–5 000 on a
 * new ticket and 1–5 000 (trimmed) on a reply,
 * ≤ 4 screenshots, none still uploading or failed (a failed tile is removed, never sent short).
 */
export function submitBlocker(draft: SupportDraft): string | null {
  if (draft.subject !== null) {
    const subject = cleanSubject(draft.subject)
    if (subject.length < SUPPORT_LIMITS.subjectMin) {
      return 'Add a short subject.'
    }
    if (subject.length > SUPPORT_LIMITS.subjectMax) {
      return `Keep the subject under ${SUPPORT_LIMITS.subjectMax} characters.`
    }
  }
  const body = draft.body.trim()
  const reply = draft.subject === null
  if (body.length < (reply ? SUPPORT_LIMITS.replyBodyMin : SUPPORT_LIMITS.bodyMin)) {
    return reply ? 'Write your reply.' : 'Tell us a little more — at least 10 characters.'
  }
  if (body.length > SUPPORT_LIMITS.bodyMax) {
    return `Keep the message under ${SUPPORT_LIMITS.bodyMax.toLocaleString('en-US')} characters.`
  }
  if (draft.images.length > SUPPORT_LIMITS.images) {
    return `Up to ${SUPPORT_LIMITS.images} screenshots.`
  }
  if (draft.images.some(image => image.status === 'uploading')) {
    return 'Wait for the screenshots to finish uploading.'
  }
  if (draft.images.some(image => image.status === 'error')) {
    return "Remove the screenshot that didn't upload."
  }
  return null
}

export function canSubmit(draft: SupportDraft): boolean {
  return submitBlocker(draft) === null
}

/** Why a picked file can't be uploaded (checked before it spends an upload), or null. */
export function imageFileProblem(file: Pick<File, 'type' | 'size'>): string | null {
  if (!SUPPORT_IMAGE_TYPES.includes(file.type)) {
    return 'Screenshots must be jpeg, png or webp.'
  }
  if (file.size > SUPPORT_LIMITS.imageBytes) {
    return 'Keep each screenshot under 4 MB.'
  }
  return null
}

/**
 * The page a ticket is about, when it is a SoundGuys page: an https URL on www.soundguys.com of
 * ≤ 500 characters, never the support pages themselves; anything else is null (DealHunt stores
 * nothing then). Hash dropped.
 */
export function capturePageUrl(candidate: string | null | undefined): string | null {
  if (!candidate || typeof candidate !== 'string') {
    return null
  }
  let url: URL
  try {
    url = new URL(candidate)
  } catch {
    return null
  }
  if (url.protocol !== 'https:' || url.host !== 'www.soundguys.com' || url.username) {
    return null
  }
  if (url.pathname === SUPPORT_PATH || url.pathname.startsWith(`${SUPPORT_PATH}/`)) {
    return null
  }
  url.hash = ''
  const href = url.href
  return href.length <= SUPPORT_LIMITS.pageUrl ? href : null
}

/** A root-relative path from `?from=` (`/x`, never `//host`, never the support pages), or null. */
function fromPath(from: string | null | undefined): string | null {
  if (typeof from !== 'string' || !from.startsWith('/') || from.startsWith('//')) {
    return null
  }
  const pathname = from.split(/[?#]/)[0]
  return pathname === SUPPORT_PATH || pathname.startsWith(`${SUPPORT_PATH}/`) ? null : from
}

/**
 * The page the reader came from. In-app navigation never updates `document.referrer`, so the
 * avatar menu passes the current path as `?from=` (`supportHref`); a full page load (an outside
 * link, a reload) falls back to the referrer. Either way only a SoundGuys URL survives.
 */
export function ticketPageUrl(from: unknown, referrer: string | null | undefined): string | null {
  const path = fromPath(typeof from === 'string' ? from : null)
  return path ? capturePageUrl(`${SITE_ORIGIN}${path}`) : capturePageUrl(referrer)
}

function time(iso: string | null | undefined): number {
  const t = iso ? Date.parse(iso) : NaN
  return Number.isFinite(t) ? t : 0
}

/** Newest activity first, then newest ticket, then id — the same order DealHunt sends. */
export function ticketSort<T extends Pick<TicketCard, 'id' | 'last_message_at' | 'created_at'>>(
  tickets: readonly T[]
): T[] {
  return [...tickets].sort(
    (a, b) =>
      time(b.last_message_at) - time(a.last_message_at) ||
      time(b.created_at) - time(a.created_at) ||
      a.id.localeCompare(b.id)
  )
}

/** "4 Oct, 14:05" in the reader's locale and zone (client render only), or '' when unreadable. */
export function supportTime(iso: string): string {
  const t = time(iso)
  if (!t) {
    return ''
  }
  return new Date(t).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}
